import { useState } from "react";
import { DeviceMobile, ShareNetwork, X } from "@phosphor-icons/react";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useInstall } from "@/lib/pwa";
import { btnGhost, btnPrimary } from "@/lib/ui";

const DISMISS_KEY = "observer.installBannerDismissed";

// Safari gives no install prompt; these are the steps, shown when someone on iOS asks.
const IOSSteps = ({ open, onClose }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="bg-[var(--o-bg2)] border-[var(--o-border)] rounded-none max-w-sm" data-testid="install-ios-steps">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">Add Observer to your Home Screen</DialogTitle>
        <DialogDescription className="text-[var(--o-muted)]">On iPhone and iPad this is done from Safari's share menu.</DialogDescription>
      </DialogHeader>
      <ol className="space-y-3 text-sm">
        <li className="flex gap-3"><span className="font-mono text-[var(--o-muted3)]">1</span> Open this page in Safari (not inside another app).</li>
        <li className="flex gap-3"><span className="font-mono text-[var(--o-muted3)]">2</span> Tap <ShareNetwork size={16} className="inline mx-1" /> Share at the bottom of the screen.</li>
        <li className="flex gap-3"><span className="font-mono text-[var(--o-muted3)]">3</span> Choose <span className="font-semibold mx-1">Add to Home Screen</span>, then Add.</li>
      </ol>
      <p className="text-xs text-[var(--o-muted3)]">It opens full screen like an app, with the same sign-in. The microphone works as it does in Safari.</p>
    </DialogContent>
  </Dialog>
);

// Menu item for the user menu: "Install app" where the browser offers a prompt, the iOS steps otherwise. Hidden once installed.
export const InstallMenuItem = () => {
  const { canPrompt, standalone, ios, prompt } = useInstall();
  const [steps, setSteps] = useState(false);
  if (standalone || (!canPrompt && !ios)) return null;
  return (
    <>
      <DropdownMenuItem onSelect={() => (canPrompt ? prompt() : setSteps(true))} className="cursor-pointer rounded-none" data-testid="header-install-item">
        <DeviceMobile size={15} className="mr-2" /> {canPrompt ? "Install app" : "Add to Home Screen"}
      </DropdownMenuItem>
      <IOSSteps open={steps} onClose={() => setSteps(false)} />
    </>
  );
};

// A one-time strip on the start page, phones only: install it, or dismiss it for good.
export const InstallBanner = () => {
  const { canPrompt, standalone, ios, prompt } = useInstall();
  const [steps, setSteps] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });
  if (standalone || dismissed || (!canPrompt && !ios)) return null;
  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* a convenience only */
    }
  };
  return (
    <div className="md:hidden flex items-center gap-3 border border-[var(--o-border)] bg-[var(--o-bg2)] px-4 py-3 mb-4 text-sm" data-testid="install-banner">
      <DeviceMobile size={18} className="shrink-0 text-[var(--o-muted)]" />
      <div className="min-w-0 flex-1">
        <div className="font-semibold">Keep Observer on your phone</div>
        <div className="text-xs text-[var(--o-muted2)]">Opens full screen from your home screen. Nothing to download from a store.</div>
      </div>
      <button type="button" onClick={() => (canPrompt ? prompt() : setSteps(true))} className={`${btnPrimary} !px-3 !py-1.5 text-xs`} data-testid="install-banner-button">{canPrompt ? "Install" : "How"}</button>
      <button type="button" onClick={dismiss} className={`${btnGhost} !px-2 !py-1.5`} aria-label="Not now" data-testid="install-banner-dismiss"><X size={14} /></button>
      <IOSSteps open={steps} onClose={() => setSteps(false)} />
    </div>
  );
};
